import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add } from "@mui/icons-material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import { usePageTitle } from "contexts/PageTitleContext";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.02),
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
    transition: 'background-color 0.2s ease-in-out',
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const AIChatBot = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    language: "",
    question: "",
    answer: "",
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [languageFilter, setLanguageFilter] = useState("");
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // Fetch supported languages
  const fetchSupportedLanguages = useCallback(async () => {
    setLoadingLanguages(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_supported_languages);
      
      if (response?.success && response?.data) {
        const languages = Array.isArray(response.data) 
          ? response.data 
          : [];
        // Sort by language_name for consistent display
        const sortedLanguages = languages.sort((a, b) => 
          (a.language_name || "").localeCompare(b.language_name || "")
        );
        setSupportedLanguages(sortedLanguages);
      } else {
        setSupportedLanguages([]);
      }
    } catch (error) {
      console.error("Error fetching supported languages:", error);
      toast.showError("Failed to load supported languages");
      setSupportedLanguages([]);
    } finally {
      setLoadingLanguages(false);
    }
  }, [toast]);

  // Fetch questions
  const fetchQuestions = useCallback(async (language = "") => {
    setLoading(true);
    try {
      let url = API_ENDPOINTS.GET_gpt_sample_questions;
      if (language) {
        url += `?language=${encodeURIComponent(language)}`;
      }
      
      const response = await apiService.get(url);
      
      if (response?.success && response?.data) {
        const questionList = Array.isArray(response.data)
          ? response.data
          : response.data.GPTSampleQuestions || response.data.questions || response.data.GPTSampleQuestion || [];
        
        setQuestions(questionList);
        
        // Extract unique languages (API uses full names like "English", "Hindi")
        const languages = [...new Set(questionList.map(q => q.language).filter(Boolean))];
        setAvailableLanguages(languages.sort());
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.showError("Failed to load questions");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Initial load - fetch supported languages and questions
  useEffect(() => {
    setPageTitle("AI Chat Bot");
    fetchSupportedLanguages();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch questions when language filter changes
  useEffect(() => {
    fetchQuestions(languageFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFilter]);

  // Open dialog for add/edit
  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingId(item.id || item.question_id);
      setFormData({
        language: item.language || "",
        question: item.question || "",
        answer: item.answer || "",
      });
    } else {
      setEditingId(null);
      setFormData({
        language: "",
        question: "",
        answer: "",
      });
    }
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      language: "",
      question: "",
      answer: "",
    });
  };

  // Save question (add or update)
  const handleSave = async () => {
    if (!formData.language?.trim() || !formData.question?.trim()) {
      toast.showError("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        language: formData.language.trim(),
        question: formData.question.trim(),
        // answer: formData.answer.trim(),
      };

      let response;
      if (editingId) {
        // Update
        payload.question_id = editingId;
        response = await apiService.put(API_ENDPOINTS.PUT_update_gpt_sample_question, payload);
      } else {
        // Add
        response = await apiService.post(API_ENDPOINTS.POST_add_gpt_sample_question, payload);
      }

      if (response?.success) {
        toast.showSuccess(editingId ? "Question updated successfully" : "Question added successfully");
        handleCloseDialog();
        fetchQuestions(languageFilter);
      } else {
        toast.showError(response?.message || "Failed to save question");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toast.showError(error?.message || "Failed to save question");
    } finally {
      setSaving(false);
    }
  };

  // Delete question
  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Question",
      message: "Are you sure you want to delete this question?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_gpt_sample_question}?question_id=${id}`
      );
      
      if (response?.success) {
        toast.showSuccess("Question deleted successfully");
        fetchQuestions(languageFilter);
      } else {
        toast.showError(response?.message || "Failed to delete question");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.showError(error?.message || "Failed to delete question");
    } finally {
      setDeletingId(null);
    }
  };

  // Filter questions by language
  const filteredQuestions = languageFilter
    ? questions.filter(q => q.language === languageFilter)
    : questions;

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            AI Chat Bot
          </Typography>
          <Muted variant="body2">Manage AI sample questions and answers</Muted>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => handleOpenDialog()}
          >
            Add Question
          </Button>
          <Button 
            startIcon={<Refresh />} 
            variant="outlined" 
            onClick={() => fetchQuestions(languageFilter)} 
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>

      {/* Language Filter */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          Filter by Language:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>All Languages</InputLabel>
          {/* <Select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            label="All Languages"
          >
            <MenuItem value="">All Languages</MenuItem>
            {availableLanguages.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </Select> */}
           <Select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              label="All Languages"
              disabled={loadingLanguages}
            >
              <MenuItem value="">All Languages</MenuItem>

              {loadingLanguages ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading languages...
                </MenuItem>
              ) : supportedLanguages.length === 0 ? (
                <MenuItem disabled>No languages available</MenuItem>
              ) : (
                supportedLanguages.map((lang) => (
                  <MenuItem key={lang.id} value={lang.language_name}>
                    {lang.language_name}
                  </MenuItem>
                ))
              )}
            </Select>

        </FormControl>
      </Box>

      {/* Questions Table */}
      <TableCard>
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2, 
            overflow: "hidden",
            boxShadow: (theme) => theme.palette.mode === 'dark' 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className="header" sx={{ width: '80px', textAlign: 'center' }}>
                  Sr No
                </StyledTableCell>
                <StyledTableCell className="header">Language</StyledTableCell>
                <StyledTableCell className="header">Question</StyledTableCell>
                {/* <StyledTableCell className="header">Answer</StyledTableCell> */}
                <StyledTableCell className="header" sx={{ width: '120px', textAlign: 'center' }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredQuestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No questions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuestions.map((item, index) => (
                  <StyledTableRow key={item.id || item.question_id}>
                    <StyledTableCell sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip 
                        label={item.language || "N/A"} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ maxWidth: 400 }}>
                        {item.question || "N/A"}
                      </Typography>
                    </StyledTableCell>
                    {/* <StyledTableCell>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          maxWidth: 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.answer || "N/A"}
                      </Typography>
                    </StyledTableCell> */}
                    <StyledTableCell>
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenDialog(item)}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(item.id || item.question_id)}
                              disabled={deletingId === (item.id || item.question_id)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                }
                              }}
                            >
                              {deletingId === (item.id || item.question_id) ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingId ? "Edit Question" : "Add Question"}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Language *</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) => setFormData((prev) => ({ ...prev, language: e.target.value }))}
                label="Language *"
                disabled={loadingLanguages}
              >
                {loadingLanguages ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading languages...
                  </MenuItem>
                ) : supportedLanguages.length === 0 ? (
                  <MenuItem disabled>No languages available</MenuItem>
                ) : (
                  supportedLanguages.map((lang) => (
                    <MenuItem key={lang.id} value={lang.language_name}>
                      {lang.language_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Question *"
              multiline
              rows={3}
              value={formData.question}
              onChange={(e) => setFormData((prev) => ({ ...prev, question: e.target.value }))}
              fullWidth
              placeholder="Enter the question..."
            />
            {/* <TextField
              label="Answer *"
              multiline
              rows={5}
              value={formData.answer}
              onChange={(e) => setFormData((prev) => ({ ...prev, answer: e.target.value }))}
              fullWidth
              placeholder="Enter the answer..."
            /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.language?.trim() || !formData.question?.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AIChatBot;

